// Import core lib dependencies.
import React 				from 'react';
import PropTypes from 'prop-types';

// Import child components.
import ProgressBar  		from '../elements/ProgressBar.jsx';
import ParticipateButton 	from '../elements/ParticipateButton.jsx';

// Misc dependencies.
import SegmentUtil			from '../../utils/segment.js';

// Config.
import Config				from '../../config/config.js';
import Enums				from '../../config/enums.js';

// Model objects.
import { Entity } 				from '../../model.js';

/**
 * <pre>
 * Number Goal type module React class.
 *
 * This component outputs markup to render a NumberGoal type component.
 * </pre>
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Goal.Components.Types
 * @namespace
 *
 * @return {module:Goal.Components.Types.NumberGoal}
 */
class NumberGoal extends React.Component {

  /**
     * Constructor.
     *
     * @param {object} props The injected props
     */
  constructor(props) {

    super(props);

    // Bind methods.
    this.checkState					= this.checkState.bind(this);
    this.checkProgress				= this.checkProgress.bind(this);
    this.participate 				= this.participate.bind(this);

    this.handleParticipate 			= this.handleParticipate.bind(this);
  }

  /**
	 * Lifecycle method.
	 *
	 */
  componentDidMount() {

    // Dispatch action to initiate goal state updates.
    this.checkState();

    // Dispatch action to initiate goal progress updates.
    this.checkProgress();
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
     * <pre>
     * Render a number Goal type.
     *
     * - bar with or without segments (display purposes only)
     * - progress indicates number of votes received
     * </pre>
     *
     * @return {module:Goal.Components.Types.NumberGoal}
     */
  render() {

    // Defaults for various elements.
    let _bar;
    let _statWrapper;
    let _messageWrapper;
    let _participateButton;

    // Define the Progress Bar segments (if any).
    const _segments = [];

    // Behave according to supported milestone strategies.
    switch (this.props.strategies.milestone) {

      // Bar only, mo milestones.
      case Enums.GOAL_MILESTONE_STRATEGY_TARGET:

        // Define a single segment.
        const _segment = SegmentUtil.buildSegmentDef(
          this.props.metricId,
          this.props.metricId,
          this.props.target,
          this.props.progress.value,
          this.props.progress.percent
        );

        // Add to segments array.
        _segments.push(_segment);

        // The Progress bar.
        _bar = this.renderProgressBar(this.props.metricId, _segments, this.props.templateOpts.orientation, this.props.templateOpts.milestoneSize, this.props.formatting);

        break;

        // Bar with visual only milestones.
      case Enums.GOAL_MILESTONE_STRATEGY_MILESTONE:

        // If we have some Milestones.
        const totalMilestones = (this.props.milestones) ? this.props.milestones.length : 0;
        if (totalMilestones) {

          // Loop over Milestones and generate segments.
          for (const milestoneKey in this.props.milestones) {

            // Grab the Milestone definition.
            const milestoneDef = this.props.milestones[milestoneKey];

            // Define basic segment data.
            const _segment = SegmentUtil.buildSegmentDef(
              milestoneKey,
              milestoneDef.id,
              milestoneDef.goal,
              this.props.progress.milestones[milestoneDef.id].value,
              this.props.progress.milestones[milestoneDef.id].percent,
              (milestoneDef.proportionOfGoal) ? milestoneDef.proportionOfGoal : null,
              milestoneDef.label,
              milestoneDef
            );

            // Additional classes to inject.
            let _wrapperClasses = 'xMilestone xMilestone'+milestoneDef.id;

            // Append Milestone reached/unreached class.
            _wrapperClasses += (this.props.state.milestones[milestoneDef.id].reached) ? ' xMilestoneReached' : ' xMilestoneNotReached';

            // Store the combined additional classes on segment.
            _segment.wrapperClasses = _wrapperClasses;

            // Add to segments array.
            _segments.push(_segment);
          }
        }

        // The Progress bar.
        _bar = this.renderProgressBar(this.props.metricId, _segments, this.props.templateOpts.orientation, this.props.templateOpts.milestoneSize, this.props.formatting);

        break;

        // Unsupported strategy.
      default:

        _bar = this.renderUnsupportedStrategy();

        break;
    }

    // The Goal stats.
    if (this.props.formatting.displayStatStrategy !== Enums.GOAL_STAT_DISPLAY_STRATEGY_NONE) {

      let _statMessage;

      // Behave according to stat display strategy.
      switch (this.props.formatting.displayStatStrategy) {

        // Just the current value.
        case Enums.GOAL_STAT_DISPLAY_STRATEGY_VALUE:

          _statMessage = this.props.labels.goal.value+': '+this.props.progress.value;

          break;

          // The current value and the goal target.
        case Enums.GOAL_STAT_DISPLAY_STRATEGY_VALUEGOAL:

          _statMessage = this.props.labels.goal.value+': '+this.props.progress.value+' '+this.props.labels.goal.valueOf+' '+this.props.target;

          break;
      }

      _statWrapper = <div className="xStatWrapper xCopyContainer">
        <p className="xStatMessage xCopy">{_statMessage}</p>
      </div>;
    }

    // The Goal/Milestone messages.
    let _goalMessage;
    if (this.props.state.messages.goal) {
      _goalMessage = <p className="xGoalMessage xCopy">{this.props.state.messages.goal}</p>;
    }
    let _milestoneMessage;
    if (this.props.state.messages.milestone) {
      _milestoneMessage = <p className="xMilestoneMessage xCopy">{this.props.state.messages.milestone}</p>;
    }

    _messageWrapper = <div className="xMessageWrapper xCopyContainer">
      {_goalMessage}
      {_milestoneMessage}
    </div>;

    // The Participate button.
    if (this.props.state.canParticipate) {

      _participateButton = <ParticipateButton handler={this.handleParticipate} alreadyParticipated={this.props.state.hasParticipated} labels={this.props.labels.button} />;
    }

    return (

      <div className="xGoalTypeWrapper xGoalTypeNumber">

        {_bar}

        <div className="xBodyContainer">

          {_statWrapper}
          {_messageWrapper}
          {_participateButton}

        </div>

      </div>
    );
  }

  /**
     * Render the Progress Bar.
     *
     * @param {String} metricId The metric id.
     * @param {Array} segments The array of segment objects.
     * @param {String} orientation The progress bar orientation.
     * @param {String} formatting The formatting options.
     * @return {module:Goal.Components.Elements.ProgressBar}
     */
  renderProgressBar(metricId, segments, orientation, segmentSize, formatting) {

    return <ProgressBar metricId={metricId}
      segments={segments}
      orientation={orientation}
      segmentSize={segmentSize}
      formatting={formatting} />;
  }

  /**
     * Render an unsupported strategy error.
     *
     * @return {ReactElement}
     */
  renderUnsupportedStrategy() {

    return <p className="xCopy xError">[UNSUPPORTED MILESTONE STRATEGY ({this.props.strategies.milestone}) FOR THIS GOAL TYPE ({this.props.type})]</p>;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
	 * Handler called when participate button is clicked.
	 *
	 */
  handleParticipate(event) {

    // Call participate method.
    this.participate(event);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Action dispatcher proxy methods */

  /**
	 * Dispatch action to check goal state.
	 *
	 */
  checkState() {

    // Define arguments specific to this Goal type.
    const goalTypeArgs = {
      milestones: this.props.milestones
    };

    // Call injected handler.
    this.props.eventHandlers.checkState(goalTypeArgs);
  }

  /**
	 * Dispatch action to check goal progress.
	 *
	 */
  checkProgress() {

    // Define arguments specific to this Goal type.
    const goalTypeArgs = {
      milestones: this.props.milestones
    };

    // Call injected handler.
    this.props.eventHandlers.checkProgress(goalTypeArgs);
  }

  /**
	 * Dispatch action to participate.
	 *
	 */
  participate(event) {

    // Define arguments specific to this Goal type.
    const goalTypeArgs = {
      milestones: this.props.milestones
    };

    // Call injected handler.
    this.props.eventHandlers.participate(event, goalTypeArgs);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */
}

/**
 * React PropTypes.
 *
 * @property {Number} metricId The metric id
 * @property {String} type The metric type
 * @property {module:Goal.Model.Entity.Strategies} strategies Object defining the various strategies in use (eg. for milestones, fraud prevention, overflow)
 * @property {module:Goal.Model.Entity.Dates} dates Object defining the start and end dates for the metric
 * @property {module:Goal.Model.Entity.TemplateOptions} templateOpts Object defining the various module template options
 * @property {(Array<module:Goal.Model.Entity.Milestone>|Array<module:Goal.Model.Entity.GatedMilestone>)} milestones Array containing the milestone definition objects
 * @property {Object} labels Object defining a k/v set of localized labels
 * @property {module:Goal.Model.Entity.Formatting} formatting Object defining a various formatting options (eg. no of decimal places to show progress percentages)
 * @property {Object} eventHandlers Object defining event handler methods.
 * @property {Object} state Object defining the current goal state.
 * @property {Object} progress Object defining the current goal progress.
 * @property {Number} target The target goal numeric value.
 */
NumberGoal.propTypes = {
  metricId: 		   PropTypes.number,
  type: 			      PropTypes.string,
  strategies: 	  PropTypes.instanceOf(Entity.Strategies),
  dates: 			     PropTypes.instanceOf(Entity.Dates),
  templateOpts: 	PropTypes.instanceOf(Entity.TemplateOptions),
  milestones: 	  PropTypes.arrayOf(PropTypes.instanceOf(Entity.Milestone)),
  labels: 		     PropTypes.object,
  formatting: 	  PropTypes.instanceOf(Entity.Formatting),
  eventHandlers:  PropTypes.object,
  state:  		     PropTypes.object,
  progress:  		  PropTypes.object,
  target: 		     PropTypes.number
};

/**
 * React default props.
 *
 * @type {Object}
 */
NumberGoal.defaultProps = {
};

// Export the dumb component.
export default NumberGoal;
